import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  FormControlLabel, 
  FormGroup,
  Checkbox
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';

import {
  firstDayofYear,
  getFirstDayOfYear,
  getLastDayOfYear,
  lastDayofYear,
  subYearStrDt,
} from '../../../utils/calcDatetime';
import SearchChkPrjImpt from '../form/checkbox/SearchChkPrjImpt';
import CommonDatePicker from '../util/CommonDatePicker';
import SearchCodeSelectbox from '../form/selectbox/SearchCodeSelectbox';

export const ShareSearchBox = ({ isLevel = false, isForeCast = false, onAddClick, formik, isAllChecked, setIsAllChecked }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openFilter, setOpenFilter] = useState(false);

  const toggleFilter = () => setOpenFilter(!openFilter);

  const onChangeDate = (newValue, name) => {
    formik.setFieldValue(name, newValue);
  };

  const handleChangeThisYear = () => {
    formik.setFieldValue('searchStrtDt', firstDayofYear);
    formik.setFieldValue('searchEndDt', lastDayofYear);
  };

  const handleChangeLastYear = () => {
    const paramDate = subYearStrDt(1);
    formik.setFieldValue('searchStrtDt', getFirstDayOfYear(paramDate));
    formik.setFieldValue('searchEndDt', getLastDayOfYear(paramDate));
  };

  
  const savedUser = useMemo(() =>
    JSON.parse(localStorage.getItem('user') || '{}'),
  []);
  

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    const finalUuid = isAllChecked ? (savedUser?.dptInf?.parentDptUuid || '') : ''; 
    formik.setFieldValue('crtHqUuid', finalUuid);
    formik.handleSubmit();
    if (isMobile) setOpenFilter(false);
  };

  // 공통 입력 필드 렌더링 함수
  const renderSearchFields = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        justifyContent: { md: 'flex-end' }, // [수정] 데스크탑에서 필드들을 우측 정렬
        gap: { xs: 2, md: 1 },
        width: '100%',
        height: isMobile ? 'auto' :'50px'
      }}
    >
      {/* 프로젝트 중요도 - 우측 정렬 유지 */}
      
        {isLevel ? (
          <Box sx={{ width: { xs: '100%', md: '250px'} }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}> {/* 하단 라인 기준 정렬 */}
              <FormControlLabel 
                control={
                  <Checkbox 
                    size="small" 
                    sx={{ p: 0, mr: 0.5 }}
                    checked={isAllChecked} 
                    onChange={(e) => {
                      setIsAllChecked(e.target.checked);
                    }}
                  />
                }
                label="전체" 
                sx={{ 
                  ml: -0.5, 
                  mr: 1, 
                  mb: '4px', 
                  flexShrink: 0, 
                  '& .MuiTypography-root': { fontSize: '0.875rem', whiteSpace: 'nowrap' } 
                }} 
              />
              <SearchChkPrjImpt
                label="프로젝트 중요도"
                displayEmpty
                name="searchPrjImpt"
                onChangeChecked={(list) => formik.setFieldValue('searchPrjImpt', list)}
                checkedList={formik.values?.searchPrjImpt}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ width: { xs: '100%', md: '200px'} }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}> {/* 하단 라인 기준 정렬 */}
              <FormControlLabel 
                control={
                  <Checkbox 
                    size="small" 
                    sx={{ p: 0, mr: 0.5 }}
                    checked={isAllChecked} 
                    onChange={(e) => {
                      setIsAllChecked(e.target.checked);
                    }}
                  />
                }
                label="전체" 
                sx={{ 
                  ml: -0.5, 
                  mr: 1, 
                  mb: '4px', 
                  flexShrink: 0, 
                  '& .MuiTypography-root': { fontSize: '0.875rem', whiteSpace: 'nowrap' } 
                }} 
              />
              <SearchCodeSelectbox
                formik={formik}
                variant="standard"
                name="searchPrjImpt"
                label="프로젝트 중요도"
                filterObject={{ blcCd: 'BASE', mlcCd: 'FORCAST', slcCd: 'PRJ_IMPT' }}
                onChangeChecked={(list) => formik.setFieldValue('searchPrjImpt', list)}
                sx={{ flex: 1 }} 
              />
            </Box>
        </Box>
        )}
      

      <TextField
        variant="standard"
        select
        name="searchKeyword"
        label="검색키워드"
        value={formik.values.searchKeyword}
        onChange={formik.handleChange}
        sx={{ width: { xs: '100%', md: '14ch' } }}
      >
        <MenuItem value="cstNm">사업명</MenuItem>
        <MenuItem value="vendorCmpNm">매출처명</MenuItem>
        <MenuItem value="crtDptNm">부서</MenuItem>
        <MenuItem value="crtNm">영업담당자</MenuItem>
        <MenuItem value="cstStatus">상태</MenuItem>
      </TextField>

      <TextField
        variant="standard"
        name="searchWords"
        label="검색어"
        value={formik.values.searchWords}
        onChange={formik.handleChange}
        sx={{ width: { xs: '100%', md: '14ch' } }}
      />
      <Box sx={{ 
        display: 'flex', 
        gap: 1, 
        width: { xs: '110%', md: 'auto' }, // 모바일에서 전체 너비를 차지해 줄바꿈 유도
        justifyContent: 'space-between' 
      }}>
    <CommonDatePicker
      label="매출 시작일"
      name="searchStrtDt"
      value={formik.values.searchStrtDt}
      formik={formik}
      onChange={onChangeDate}
      // 모바일에서 두 개가 한 줄에 들어가도록 너비 축소 (예: 48%)
      sx={{ width: { xs: '48%', md: '14ch' } }} 
    />
    <CommonDatePicker
      label="매출 종료일"
      name="searchEndDt"
      value={formik.values.searchEndDt}
      formik={formik}
      onChange={onChangeDate}
      sx={{ width: { xs: '48%', md: '14ch' } }}
    />
  </Box>
    </Box>
  );

  return (
    <Box sx={{ my: 1 }}>
      {isMobile ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', // 위아래로 배치
          gap: 1,                  // 버튼 사이 세로 간격
          width: '100%' 
        }}>
          <Button
            fullWidth
            variant="outlined"
            color="info"
            startIcon={<FilterListIcon />}
            onClick={toggleFilter}
          >
            검색 필터 설정
          </Button>
        </Box>
      ) : (
        /* --- 데스크탑 레이아웃 (모든 요소를 우측으로 밀착) --- */
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-end', // [핵심] 전체 레이아웃 우측 정렬
            gap: 1, 
            width: '100%' 
          }}
        >
          {renderSearchFields()}
          
          <Box sx={{ display: 'flex', gap: 1, ml: 1, whiteSpace: 'nowrap' }}>
            <Button variant="outlined" color="info" onClick={handleChangeThisYear} size="small">
              금년
            </Button>
            <Button variant="outlined" color="info" onClick={handleChangeLastYear} size="small">
              작년
            </Button>
            <Button type="submit" variant="contained" color="info" startIcon={<SearchIcon />}>
              검색
            </Button>
            {isForeCast && (
              <Button variant="contained" color="secondary" onClick={onAddClick}>
                포캐스팅 추가
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* 모바일 검색 팝업 (내용 동일) */}
      <Dialog open={openFilter} onClose={toggleFilter} fullWidth maxWidth="xs">
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>상세 검색 필터</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ py: 1 }}>
            {renderSearchFields()}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
              <Button fullWidth variant="outlined" color="info" onClick={handleChangeThisYear}>
                금년 데이터
              </Button>
              <Button fullWidth variant="outlined" color="info" onClick={handleChangeLastYear}>
                작년 데이터
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={toggleFilter} sx={{ color: 'text.secondary', flex: 1 }}>
            닫기
          </Button>
          <Button onClick={handleSearchSubmit} variant="contained" color="info" sx={{ flex: 2 }}>
            적용
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
